import { FC, MouseEvent } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { LocalizedText, useDialog, withScopeContext } from '@mews/framework';
import { IconName } from '@optimus-web/icons';
import { DropdownItem, Icon } from '@optimus-web/core';
import { TextKey } from '@mews-commander/enums';
import { OrderItem, WithScope } from '@mews-commander/types';
import { OrderItem as GeneratedOrderItem, OrderItemType } from '@mews-commander/data-access';
import { useEnterpriseBuildLocation } from '@mews-commander/ui';
import { getCurrentEnterpriseSubscriptionId } from '@mews-commander/legacy-entities/src/selectors/session/enterprise';
import { areNewActionLogsEnabled } from '@mews-commander/legacy-entities/src/ducks';
import { getOrderItemAttachedReservationId } from '@mews-commander/legacy-entities/src/ducks/orderItems';
import { useCommanderSelector } from '@mews-commander/utils';
import { openItemAssignmentDialog } from '../../../../ducks/itemAssignment';
import { openModal } from '../../../../../app';
import CancelBillItemDialog from '../../../../CancelBillItemDialog/CancelBillItemDialog';
import { EditItemDialog } from '../../../../EditItemDialog/EditItemDialog';
import { OrderItemIconMenuItem } from './OrderItemIconMenuItem';
import { OrderItemDropdownButton } from './OrderItemIconMenu';

interface OrderItemContextMenuProps extends WithScope {
    orderItem: GeneratedOrderItem;
}

const OrderItemContextMenu: FC<OrderItemContextMenuProps> = ({ orderItem, scope }) => {
    const { id: orderItemId, billId } = orderItem;
    const dispatch = useDispatch();
    const buildLocation = useEnterpriseBuildLocation();

    const [cancelItemDialogProps, toggleCancelItemDialog] = useDialog();
    const [editItemDialogProps, toggleEditItemDialog] = useDialog();

    const isOrderItemActionLogEnabled = useSelector((state) => {
        const subscriptionId: string = getCurrentEnterpriseSubscriptionId(state);
        return areNewActionLogsEnabled(state, { subscriptionId });
    });

    const attachedReservationId = useCommanderSelector((state) =>
        getOrderItemAttachedReservationId(state, { orderItemId: orderItem.id })
    );

    const onActionLogClick = (e: MouseEvent) => {
        e.stopPropagation();
        dispatch(openModal(buildLocation({ to: 'OrderItem/ActionLog/$id', params: { id: orderItem.id } }).href));
    };

    const onMoveItemClick = (e: MouseEvent) => {
        e.stopPropagation();
        dispatch(openItemAssignmentDialog({ billId: null, scope, orderItemIds: [orderItemId] }));
    };

    const onEditItemClick = (e: MouseEvent) => {
        e.stopPropagation();
        toggleEditItemDialog();
    };

    const onViewReservationClick = (e: MouseEvent) => {
        e.stopPropagation();

        if (attachedReservationId) {
            const url = buildLocation({
                to: 'Reservation/Detail/$reservationId/Status',
                params: { reservationId: attachedReservationId },
            }).href;
            dispatch(openModal(url));
        }
    };

    const onCancelItemClick = (e: MouseEvent) => {
        e.stopPropagation();

        if (!billId) return null;

        toggleCancelItemDialog();
    };

    const isAllowance =
        orderItem.data.discriminator === OrderItemType.ProductOrder && orderItem.data.value.productType === 'Allowance';
    const isOrderItemEditable = orderItem.isPriceEditable || orderItem.isQuantityEditable;

    return (
        <>
            <CancelBillItemDialog
                orderItem={orderItem as unknown as OrderItem}
                billId={billId as string}
                scope={scope}
                {...cancelItemDialogProps}
            />
            <EditItemDialog orderItem={orderItem} {...editItemDialogProps} />
            <OrderItemDropdownButton
                icon={IconName.More}
                overlayMinWidth={250}
                data-order-item-menu
                placement="bottom-end"
                variant="ghost"
                size="medium"
            >
                {attachedReservationId ? (
                    <OrderItemIconMenuItem
                        leftDetail={<Icon value={IconName.Reservations} />}
                        label={<LocalizedText textKey={TextKey.ViewReservation} />}
                        onClick={onViewReservationClick}
                        data-order-item-view-reservation
                    />
                ) : null}
                {isOrderItemEditable && !isAllowance ? (
                    <OrderItemIconMenuItem
                        leftDetail={<Icon value={IconName.Edit} />}
                        label={<LocalizedText textKey={TextKey.EditItem} />}
                        onClick={onEditItemClick}
                        data-order-item-edit
                    />
                ) : null}
                {orderItem.isCancelable && !isAllowance ? (
                    <OrderItemIconMenuItem
                        leftDetail={<Icon value={IconName.Delete} />}
                        label={<LocalizedText textKey={TextKey.RemoveItem} />}
                        onClick={onCancelItemClick}
                        data-order-item-remove
                    />
                ) : null}
                {orderItem.isMovable ? (
                    <DropdownItem
                        leftDetail={<Icon value={IconName.MovePayment} />}
                        label={<LocalizedText textKey={TextKey.MoveItem} />}
                        onClick={onMoveItemClick}
                        data-order-item-move
                    />
                ) : null}
                {isOrderItemActionLogEnabled ? (
                    <DropdownItem
                        leftDetail={<Icon value={IconName.ActionLog} />}
                        label={<LocalizedText textKey={TextKey.ActionLog} />}
                        onClick={onActionLogClick}
                        data-order-item-action-log
                    />
                ) : null}
            </OrderItemDropdownButton>
        </>
    );
};

export default withScopeContext(OrderItemContextMenu);
